import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { SPORTS, getSportByName } from '../../utils/sports';
import ImageUploader from '../components/ImageUploader';
import AdminDarkSelect from '../components/AdminDarkSelect';
import AdminModal from '../components/AdminModal';
import {
  ALL_SIZES,
  PRODUCT_STATUSES,
  createProduct,
  fetchAdminProduct,
  fetchBrands,
  fetchStockHistory,
  getProductStock,
  logStockChange,
  replaceProductImages,
  replaceProductSizes,
  replaceProductVariants,
  slugify,
  updateProduct,
} from '../lib/adminApi';
import {
  buildProductSeoTitle,
  isCustomSeoTitle,
} from '../../utils/seo';

const APPAREL_OPTIONS = [
  { value: '', label: 'Select category' },
  { value: 'headwear', label: 'Headwear' },
  { value: 'sweaters', label: 'Sweaters' },
  { value: 'shirts', label: 'Shirts' },
  { value: 'shorts-pants', label: 'Shorts & Pants' },
  { value: 'footwear', label: 'Footwear' },
  { value: 'accessories', label: 'Accessories' },
];

const GENDER_OPTIONS = [
  { value: 'unisex', label: 'Unisex' },
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
];

const emptyForm = {
  name: '',
  brand: '',
  brand_slug: '',
  team: '',
  league: '',
  sport: '',
  category: '',
  apparel_type: '',
  gender: 'unisex',
  price: '',
  sale_price: '',
  description: '',
  sku: '',
  tags: '',
  status: 'published',
  seo_title: '',
  meta_description: '',
  url_slug: '',
  featured: false,
  new_arrivals: false,
  trending: false,
  recommended: false,
  show_on_homepage: false,
  hide_product: false,
  allow_search: true,
  show_in_category: true,
  reserved_stock: 0,
  color: '#f2f2f2',
};

function buildDefaultSizes() {
  return ALL_SIZES.map(label => ({
    size_label: label,
    stock_quantity: 0,
    sku: '',
    enabled: true,
  }));
}

function ProductEditor() {
  const { productId } = useParams();
  const isNew = !productId || productId === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState(emptyForm);
  const [images, setImages] = useState([]);
  const [sizes, setSizes] = useState(buildDefaultSizes());
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stockModal, setStockModal] = useState(null);
  const [stockAmount, setStockAmount] = useState('');
  const [stockHistory, setStockHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [seoTitleManual, setSeoTitleManual] = useState(false);
  const [brandOptions, setBrandOptions] = useState([]);

  const sportOptions = useMemo(() => SPORTS.map(sport => sport.name), []);

  useEffect(() => {
    let cancelled = false;
    fetchBrands()
      .then(rows => {
        if (!cancelled) setBrandOptions(rows || []);
      })
      .catch(() => {
        if (!cancelled) setBrandOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const brandSelectOptions = useMemo(
    () => brandOptions.map(brand => brand.name),
    [brandOptions]
  );

  const selectedSport = useMemo(
    () => getSportByName(form.sport || form.category),
    [form.sport, form.category]
  );

  const leagueOptions = useMemo(() => {
    if (!selectedSport?.leagues?.length) return [];
    return selectedSport.leagues.map(league => league.name);
  }, [selectedSport]);

  const teamOptions = useMemo(() => {
    if (!selectedSport) return [];

    const leagueName = (form.league || '').trim().toLowerCase();
    let options = [];
    if (leagueName && selectedSport.leagues?.length) {
      const league = selectedSport.leagues.find(
        item => item.name.toLowerCase() === leagueName
      );
      if (league?.teams?.length) options = [...league.teams];
    }

    if (!options.length) {
      const fromLeagues = (selectedSport.leagues || []).flatMap(league => league.teams || []);
      const fromSport = selectedSport.teams || [];
      options = [...new Set([...fromLeagues, ...fromSport])].sort((a, b) =>
        a.localeCompare(b)
      );
    }

    const currentTeam = (form.team || '').trim();
    if (
      currentTeam &&
      !options.some(option => option.toLowerCase() === currentTeam.toLowerCase())
    ) {
      options = [currentTeam, ...options];
    }

    return options;
  }, [selectedSport, form.league, form.team, form.brand]);

  const apparelOptions = useMemo(
    () => APPAREL_OPTIONS.filter(option => option.value),
    []
  );

  const statusOptions = useMemo(
    () =>
      PRODUCT_STATUSES.map(status => ({
        value: status,
        label: status.replace(/_/g, ' '),
      })),
    []
  );

  useEffect(() => {
    if (isNew) return undefined;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const product = await fetchAdminProduct(productId);
        if (cancelled) return;

        const sportName =
          getSportByName(product.sport || product.category)?.name ||
          product.sport ||
          product.category ||
          '';

        const loadedName = product.name || '';
        const loadedSeo = product.seo_title || '';
        setSeoTitleManual(isCustomSeoTitle(loadedSeo, loadedName));

        setForm({
          name: loadedName,
          brand: product.brand || '',
          brand_slug: product.brand_slug || '',
          team: product.team || '',
          league: product.league || '',
          sport: sportName,
          category: sportName,
          apparel_type: product.apparel_type || '',
          gender: product.gender || 'unisex',
          price: product.price ?? '',
          sale_price: product.sale_price ?? '',
          description: product.description || '',
          sku: product.sku || '',
          tags: Array.isArray(product.tags) ? product.tags.join(', ') : '',
          status: product.status || 'published',
          seo_title: loadedSeo || buildProductSeoTitle(loadedName),
          meta_description: product.meta_description || '',
          url_slug: product.url_slug || '',
          featured: Boolean(product.featured),
          new_arrivals: Boolean(product.new_arrivals),
          trending: Boolean(product.trending),
          recommended: Boolean(product.recommended),
          show_on_homepage: Boolean(product.show_on_homepage),
          hide_product: Boolean(product.hide_product),
          allow_search: product.allow_search !== false,
          show_in_category: product.show_in_category !== false,
          reserved_stock: product.reserved_stock || 0,
          color: product.color || '#f2f2f2',
        });

        setImages(
          (product.images || []).map(img => ({
            ...img,
            is_thumbnail: Boolean(img.is_thumbnail),
          }))
        );

        const sizeMap = Object.fromEntries(
          (product.sizes || []).map(s => [s.size_label, s])
        );
        setSizes(
          ALL_SIZES.map(label => ({
            size_label: label,
            stock_quantity: sizeMap[label]?.stock_quantity ?? 0,
            sku: sizeMap[label]?.sku || '',
            enabled: sizeMap[label]?.enabled !== false,
          }))
        );

        setVariants(product.variants || []);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Could not load product.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [isNew, productId]);

  const stock = useMemo(
    () => getProductStock({ product_sizes: sizes, reserved_stock: form.reserved_stock }),
    [sizes, form.reserved_stock]
  );

  const setField = (key, value) => {
    setForm(current => ({ ...current, [key]: value }));
  };

  const handleNameChange = value => {
    setForm(current => ({
      ...current,
      name: value,
      seo_title: seoTitleManual ? current.seo_title : buildProductSeoTitle(value),
    }));
  };

  const handleSeoTitleChange = value => {
    setSeoTitleManual(true);
    setField('seo_title', value);
  };

  const resetSeoTitle = () => {
    setSeoTitleManual(false);
    setField('seo_title', buildProductSeoTitle(form.name));
  };

  const handleSportChange = value => {
    const sport = getSportByName(value);
    const leagueNames = (sport?.leagues || []).map(league => league.name);
    const teamNames = [
      ...(sport?.teams || []),
      ...(sport?.leagues || []).flatMap(league => league.teams || []),
    ];

    setForm(current => {
      const leagueStillValid = leagueNames.some(
        name => name.toLowerCase() === (current.league || '').trim().toLowerCase()
      );
      const teamValue = current.team || '';
      const teamStillValid = teamNames.some(
        name => name.toLowerCase() === teamValue.trim().toLowerCase()
      );

      return {
        ...current,
        sport: value,
        category: value,
        league: leagueStillValid ? current.league : '',
        team: teamStillValid ? teamValue : '',
      };
    });
  };

  const handleLeagueChange = value => {
    setForm(current => {
      const sport = getSportByName(current.sport || current.category);
      const league = (sport?.leagues || []).find(
        item => item.name.toLowerCase() === value.trim().toLowerCase()
      );
      const teamValue = current.team || '';
      const teamStillValid =
        !value ||
        !league?.teams?.length ||
        league.teams.some(
          name => name.toLowerCase() === teamValue.trim().toLowerCase()
        );

      return {
        ...current,
        league: value,
        team: teamStillValid ? teamValue : '',
      };
    });
  };

  const handleTeamChange = value => {
    setField('team', value);
  };

  const updateSize = (label, patch) => {
    setSizes(current =>
      current.map(size => (size.size_label === label ? { ...size, ...patch } : size))
    );
  };

  const validate = () => {
    if (!form.name.trim()) return 'Product name is required.';
    if (!(form.sport.trim() || form.category.trim())) return 'Sport is required.';
    if (form.price === '' || Number.isNaN(Number(form.price))) return 'Valid price is required.';
    if (Number(form.price) < 0) return 'Price cannot be negative.';
    if (form.sale_price !== '' && Number(form.sale_price) < 0) return 'Sale price cannot be negative.';
    if (variants.length > 1 && variants.some(variant => !String(variant.name || '').trim())) {
      return 'Each colour needs a name when you offer more than one colour.';
    }
    return '';
  };

  const addColour = () => {
    const newId = `temp-${Date.now()}`;

    setVariants(current => {
      if (current.length === 0) {
        return [
          {
            id: newId,
            name: '',
            color_hex: '#111111',
            is_default: true,
          },
        ];
      }

      return [
        ...current,
        {
          id: newId,
          name: '',
          color_hex: '#111111',
          is_default: false,
        },
      ];
    });

    // First colour: attach any shared gallery images to that colour.
    if (variants.length === 0) {
      setImages(current =>
        current.map(img =>
          img.variant_id ? img : { ...img, variant_id: newId }
        )
      );
    }
  };

  const removeColour = index => {
    setVariants(current => {
      const removed = current[index];
      const next = current.filter((_, i) => i !== index);

      if (removed?.is_default && next.length && !next.some(v => v.is_default)) {
        next[0] = { ...next[0], is_default: true };
      }

      // Last colour removed → images become a simple product gallery again.
      if (next.length === 0) {
        setImages(imgs => imgs.map(img => ({ ...img, variant_id: null })));
        return next;
      }

      setImages(imgs =>
        imgs.map(img =>
          img.variant_id === removed?.id ? { ...img, variant_id: null } : img
        )
      );
      return next;
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const thumbnail = images.find(img => img.is_thumbnail) || images[0];
      const sportValue =
        getSportByName(form.sport || form.category)?.name ||
        form.sport.trim() ||
        form.category.trim();
      const teamValue = form.team.trim() || null;
      const selectedBrand = brandOptions.find(
        b => b.slug === form.brand_slug || b.name === form.brand_slug
      );
      const payload = {
        name: form.name.trim(),
        brand: selectedBrand?.name || null,
        brand_slug: selectedBrand?.slug || form.brand_slug.trim() || null,
        team: teamValue,
        league: form.league.trim() || null,
        sport: sportValue,
        category: sportValue,
        apparel_type: form.apparel_type || null,
        gender: form.gender || 'unisex',
        price: Number(form.price),
        sale_price: form.sale_price === '' ? null : Number(form.sale_price),
        description: form.description.trim() || null,
        sku: form.sku.trim() || null,
        tags: form.tags
          .split(',')
          .map(t => t.trim())
          .filter(Boolean),
        status: form.status,
        seo_title:
          form.seo_title.trim() || buildProductSeoTitle(form.name.trim()) || null,
        meta_description: form.meta_description.trim() || null,
        url_slug: (form.url_slug.trim() || slugify(form.name)) || null,
        featured: form.featured,
        new_arrivals: form.new_arrivals,
        trending: form.trending,
        recommended: form.recommended,
        show_on_homepage: form.show_on_homepage,
        hide_product: form.hide_product,
        allow_search: form.allow_search,
        show_in_category: form.show_in_category,
        reserved_stock: Number(form.reserved_stock) || 0,
        color: form.color || '#f2f2f2',
        image_url: thumbnail?.image_url || null,
      };

      let savedId = productId;

      if (isNew) {
        const created = await createProduct(payload);
        savedId = created.id;
      } else {
        await updateProduct(productId, payload);
      }

      // Single-colour products: no colour variants on the storefront.
      const multiColour = variants.length > 1;
      const variantsForSave = multiColour ? variants : [];
      const savedVariants = await replaceProductVariants(savedId, variantsForSave);

      const variantIdMap = {};
      if (multiColour) {
        variantsForSave.forEach((v, i) => {
          if (v.id && String(v.id).startsWith('temp-') && savedVariants[i]) {
            variantIdMap[v.id] = savedVariants[i].id;
          }
        });
      }

      const imagesForSave = images.map(img => ({
        ...img,
        variant_id: multiColour
          ? variantIdMap[img.variant_id] || img.variant_id || null
          : null,
      }));

      await replaceProductImages(savedId, imagesForSave);
      await replaceProductSizes(savedId, sizes);

      setSuccess('Product saved.');
      if (isNew) {
        navigate(`/admin/products/${savedId}`, { replace: true });
      } else {
        setVariants(savedVariants);
        setImages(
          imagesForSave.map((img, index) => ({
            ...img,
            id: img.id?.startsWith?.('temp-') ? `saved-${index}` : img.id,
          }))
        );
      }
    } catch (err) {
      setError(err.message || 'Could not save product.');
    } finally {
      setSaving(false);
    }
  };

  const openStockModal = (mode) => {
    setStockAmount('');
    setStockModal(mode);
  };

  const applyStockChange = async () => {
    const amount = Number(stockAmount);
    if (!amount || Number.isNaN(amount)) {
      setError('Enter a valid stock amount.');
      return;
    }

    const delta = stockModal === 'restock' ? Math.abs(amount) : amount;
    const nextSizes = sizes.map((size, index) => {
      if (index !== 0 && stockModal === 'adjust') {
        return size;
      }
      if (stockModal === 'restock') {
        return { ...size, stock_quantity: Number(size.stock_quantity) + Math.abs(amount) };
      }
      // adjust: apply to first size as simple adjust, or distribute
      return size;
    });

    if (stockModal === 'adjust') {
      // Adjust total by changing M size preferentially, else first enabled
      const target =
        nextSizes.find(s => s.size_label === 'M') ||
        nextSizes.find(s => s.enabled) ||
        nextSizes[0];
      setSizes(current =>
        current.map(s =>
          s.size_label === target.size_label
            ? { ...s, stock_quantity: Math.max(0, Number(s.stock_quantity) + delta) }
            : s
        )
      );
    } else {
      setSizes(nextSizes);
    }

    if (!isNew) {
      try {
        await logStockChange({
          productId,
          sizeLabel: stockModal === 'adjust' ? 'M' : null,
          changeAmount: delta,
          reason: stockModal === 'restock' ? 'Restock' : 'Manual adjust',
          userId: user?.id,
        });
      } catch {
        // history is best-effort until product is saved
      }
    }

    setStockModal(null);
    setSuccess(stockModal === 'restock' ? 'Stock restocked (save to persist).' : 'Stock adjusted (save to persist).');
  };

  const openHistory = async () => {
    if (isNew) {
      setError('Save the product before viewing stock history.');
      return;
    }
    try {
      const rows = await fetchStockHistory(productId);
      setStockHistory(rows);
      setHistoryOpen(true);
    } catch (err) {
      setError(err.message || 'Could not load stock history.');
    }
  };

  if (loading) {
    return <p className="admin-empty">Loading product editor...</p>;
  }

  return (
    <div>
      <div className="admin-topbar" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="admin-page-title">{isNew ? 'Add product' : 'Edit product'}</h1>
          <p className="admin-page-subtitle">
            Manage details, gallery, sizes, variants, and visibility in one place
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/admin/products" className="admin-btn admin-btn-secondary">Back</Link>
          <button
            type="submit"
            form="product-editor-form"
            className="admin-btn admin-btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save product'}
          </button>
        </div>
      </div>

      {error && <p className="admin-error">{error}</p>}
      {success && <p className="admin-success-msg">{success}</p>}
      {form.status !== 'published' && (
        <p className="admin-error">
          Status is “{form.status.replace(/_/g, ' ')}”. This product will not appear in the
          storefront (category pages, search, or product links) until status is Published.
        </p>
      )}

      <form id="product-editor-form" onSubmit={handleSave}>
        <div className="admin-card">
          <h2 className="admin-card-title">Basic information</h2>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label className="admin-label">Product name</label>
              <input
                className="admin-input"
                value={form.name}
                onChange={e => handleNameChange(e.target.value)}
                required
              />
            </div>
            <AdminDarkSelect
              label="Team"
              value={form.team}
              onChange={handleTeamChange}
              options={teamOptions}
              placeholder="e.g. Mercedes, Arsenal"
              disabled={!selectedSport}
            />
            <AdminDarkSelect
              label="Brand"
              value={
                brandOptions.find(b => b.slug === form.brand_slug)?.name || ''
              }
              onChange={value => {
                const match = brandOptions.find(
                  b => b.name.toLowerCase() === value.trim().toLowerCase()
                );
                setField('brand_slug', match?.slug || '');
              }}
              options={brandSelectOptions}
              placeholder="Select licensed brand"
              allowCustom={false}
            />
            <p style={{ gridColumn: '1 / -1', margin: '-8px 0 0', color: '#777', fontSize: 12 }}>
              Licensed brand for footer / brand pages — add brands under Sports and Brands in admin.
              Not the same as team.
            </p>
            <AdminDarkSelect
              label="Sport"
              value={selectedSport?.name || form.sport || form.category}
              onChange={handleSportChange}
              options={sportOptions}
              placeholder="e.g. Formula Series, Football"
              required
              allowCustom={false}
            />
            <AdminDarkSelect
              label="League (optional)"
              value={form.league}
              onChange={handleLeagueChange}
              options={
                form.league &&
                !leagueOptions.some(
                  option => option.toLowerCase() === form.league.trim().toLowerCase()
                )
                  ? [form.league, ...leagueOptions]
                  : leagueOptions
              }
              placeholder="e.g. Premier League, NBA"
              disabled={!selectedSport || leagueOptions.length === 0}
            />
            <AdminDarkSelect
              label="Apparel category"
              value={form.apparel_type}
              onChange={value => setField('apparel_type', value)}
              options={apparelOptions}
              placeholder="Select category"
              allowCustom={false}
            />
            <AdminDarkSelect
              label="Gender"
              value={form.gender}
              onChange={value => setField('gender', value)}
              options={GENDER_OPTIONS}
              allowCustom={false}
            />
            <div className="admin-field">
              <label className="admin-label">SKU</label>
              <input className="admin-input" value={form.sku} onChange={e => setField('sku', e.target.value)} />
            </div>
            <AdminDarkSelect
              label="Status"
              value={form.status}
              onChange={value => setField('status', value)}
              options={statusOptions}
              allowCustom={false}
            />
            <div className="admin-field">
              <label className="admin-label">Price</label>
              <input className="admin-input" type="number" min="0" step="0.01" value={form.price} onChange={e => setField('price', e.target.value)} required />
            </div>
            <div className="admin-field">
              <label className="admin-label">Sale price</label>
              <input className="admin-input" type="number" min="0" step="0.01" value={form.sale_price} onChange={e => setField('sale_price', e.target.value)} />
            </div>
          </div>
          <div className="admin-field">
            <label className="admin-label">Description</label>
            <textarea className="admin-textarea" value={form.description} onChange={e => setField('description', e.target.value)} />
          </div>
          <div className="admin-field">
            <label className="admin-label">Tags (comma separated)</label>
            <input className="admin-input" value={form.tags} onChange={e => setField('tags', e.target.value)} />
          </div>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Images &amp; colours</h2>
          <p style={{ margin: '0 0 16px', color: '#777', fontSize: 14 }}>
            {variants.length === 0
              ? 'Upload images for this product. Add colours below only if it comes in more than one colour.'
              : 'Each colour has its own images. Shoppers only see a colour picker when there are two or more colours.'}
          </p>

          {variants.length === 0 ? (
            <ImageUploader
              images={images}
              onChange={setImages}
              showVariantSelect={false}
            />
          ) : (
            <div className="admin-variant-list">
              {variants.map((variant, index) => {
                const variantImages = images.filter(img => img.variant_id === variant.id);

                return (
                  <div
                    key={variant.id}
                    className={`admin-variant-card${variant.is_default ? ' is-default' : ''}`}
                  >
                    <div className="admin-variant-card-head">
                      <span
                        className="admin-variant-swatch"
                        style={{ background: variant.color_hex || '#cccccc' }}
                        aria-hidden="true"
                      />
                      <div className="admin-variant-fields">
                        <div className="admin-field">
                          <label className="admin-label">Colour name</label>
                          <input
                            className="admin-input"
                            value={variant.name}
                            onChange={e =>
                              setVariants(current =>
                                current.map((v, i) =>
                                  i === index ? { ...v, name: e.target.value } : v
                                )
                              )
                            }
                            placeholder="e.g. Rosso Corsa, Midnight Black"
                            required={variants.length > 1}
                          />
                        </div>
                        <div className="admin-field">
                          <label className="admin-label">Swatch</label>
                          <div className="admin-color-row">
                            <input
                              className="admin-input admin-color-picker"
                              type="color"
                              value={variant.color_hex || '#111111'}
                              onChange={e =>
                                setVariants(current =>
                                  current.map((v, i) =>
                                    i === index ? { ...v, color_hex: e.target.value } : v
                                  )
                                )
                              }
                              aria-label={`Colour swatch for ${variant.name || `variant ${index + 1}`}`}
                            />
                            <input
                              className="admin-input"
                              value={variant.color_hex || '#111111'}
                              onChange={e =>
                                setVariants(current =>
                                  current.map((v, i) =>
                                    i === index ? { ...v, color_hex: e.target.value } : v
                                  )
                                )
                              }
                              placeholder="#111111"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="admin-variant-actions">
                        <button
                          type="button"
                          className={`admin-btn admin-btn-sm ${
                            variant.is_default ? 'admin-btn-primary' : 'admin-btn-secondary'
                          }`}
                          onClick={() => {
                            setVariants(current =>
                              current.map((v, i) => ({
                                ...v,
                                is_default: i === index,
                              }))
                            );

                            const firstVariantImage = images.find(
                              img => img.variant_id === variant.id
                            );
                            if (firstVariantImage) {
                              setImages(current =>
                                current.map(img => ({
                                  ...img,
                                  is_thumbnail: img.id === firstVariantImage.id,
                                }))
                              );
                            }
                          }}
                        >
                          {variant.is_default ? 'Opens first' : 'Open first'}
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-danger admin-btn-sm"
                          onClick={() => removeColour(index)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    {variant.is_default && variants.length > 1 && (
                      <p className="admin-variant-default-note">
                        This colour appears first when shoppers open the product.
                      </p>
                    )}

                    {variants.length === 1 && (
                      <p className="admin-variant-default-note">
                        Add at least one more colour to show a colour picker on the product page.
                        With a single colour, shoppers only see the images.
                      </p>
                    )}

                    <div className="admin-variant-images">
                      <p className="admin-label">
                        Images ({variantImages.length})
                      </p>
                      <ImageUploader
                        images={images}
                        onChange={setImages}
                        variants={variants}
                        variantId={variant.id}
                        filterToVariant
                        compact
                        showVariantSelect={false}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="admin-images-add-colour">
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={addColour}
            >
              {variants.length === 0 ? 'Add a colour' : 'Add another colour'}
            </button>
          </div>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Size inventory</h2>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Size</th>
                  <th>Quantity</th>
                  <th>SKU</th>
                  <th>Enabled</th>
                </tr>
              </thead>
              <tbody>
                {sizes.map(size => (
                  <tr key={size.size_label}>
                    <td style={{ fontWeight: 600 }}>{size.size_label}</td>
                    <td>
                      <input
                        className="admin-input"
                        type="number"
                        min="0"
                        style={{ maxWidth: 120 }}
                        value={size.stock_quantity}
                        onChange={e =>
                          updateSize(size.size_label, {
                            stock_quantity: Math.max(0, Number(e.target.value) || 0),
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="admin-input"
                        value={size.sku || ''}
                        onChange={e => updateSize(size.size_label, { sku: e.target.value })}
                        placeholder="Optional"
                      />
                    </td>
                    <td>
                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          checked={size.enabled !== false}
                          onChange={e => updateSize(size.size_label, { enabled: e.target.checked })}
                        />
                        Enabled
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Inventory summary</h2>
          <div className="admin-stats" style={{ marginBottom: 16 }}>
            <div className="admin-stat">
              <p className="admin-stat-label">Total stock</p>
              <p className="admin-stat-value">{stock.total}</p>
            </div>
            <div className="admin-stat">
              <p className="admin-stat-label">Reserved</p>
              <p className="admin-stat-value">{stock.reserved}</p>
            </div>
            <div className="admin-stat">
              <p className="admin-stat-label">Available</p>
              <p className="admin-stat-value">{stock.available}</p>
            </div>
          </div>
          <div className="admin-field" style={{ maxWidth: 240 }}>
            <label className="admin-label">Reserved stock</label>
            <input
              className="admin-input"
              type="number"
              min="0"
              value={form.reserved_stock}
              onChange={e => setField('reserved_stock', Number(e.target.value) || 0)}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="admin-btn admin-btn-secondary" onClick={() => openStockModal('restock')}>
              Restock
            </button>
            <button type="button" className="admin-btn admin-btn-secondary" onClick={() => openStockModal('adjust')}>
              Adjust stock
            </button>
            <button type="button" className="admin-btn admin-btn-secondary" onClick={openHistory}>
              Stock history
            </button>
          </div>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Homepage visibility</h2>
          <div className="admin-checkbox-row">
            {[
              ['featured', 'Featured Products'],
              ['new_arrivals', 'New Arrivals'],
              ['trending', 'Trending'],
              ['recommended', 'Recommended'],
              ['show_on_homepage', 'Homepage'],
              ['hide_product', 'Hide Product'],
              ['allow_search', 'Allow Search'],
              ['show_in_category', 'Show in Category'],
            ].map(([key, label]) => (
              <label key={key} className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(form[key])}
                  onChange={e => setField(key, e.target.checked)}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">SEO</h2>
          <div className="admin-field">
            <label className="admin-label">SEO title</label>
            <input
              className="admin-input"
              value={form.seo_title}
              onChange={e => handleSeoTitleChange(e.target.value)}
              placeholder={buildProductSeoTitle(form.name)}
            />
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#888', lineHeight: 1.45 }}>
              {seoTitleManual
                ? 'Custom title — editing the product name will not overwrite this.'
                : 'Auto from product name + “Laurenti Apparel”. Long names are shortened at a word boundary (~60 chars).'}
              {' '}
              <span style={{ color: form.seo_title.length > 60 ? '#b45309' : '#888' }}>
                {form.seo_title.length}/60
              </span>
              {seoTitleManual && (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={resetSeoTitle}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      color: '#111',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      font: 'inherit',
                    }}
                  >
                    Reset to automatic
                  </button>
                </>
              )}
            </p>
          </div>
          <div className="admin-field">
            <label className="admin-label">Meta description</label>
            <textarea className="admin-textarea" value={form.meta_description} onChange={e => setField('meta_description', e.target.value)} />
          </div>
          <div className="admin-field">
            <label className="admin-label">URL slug</label>
            <input
              className="admin-input"
              value={form.url_slug}
              onChange={e => setField('url_slug', slugify(e.target.value))}
              placeholder={slugify(form.name)}
            />
          </div>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Product status</h2>
          <div className="admin-field" style={{ maxWidth: 280 }}>
            <label className="admin-label">Status</label>
            <select className="admin-input" value={form.status} onChange={e => setField('status', e.target.value)}>
              {PRODUCT_STATUSES.map(status => (
                <option key={status} value={status}>
                  {status.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
          {form.status !== 'published' && (
            <p style={{ margin: 0, color: '#b45309', fontSize: 14 }}>
              Only Published products are visible to customers.
            </p>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <Link to="/admin/products" className="admin-btn admin-btn-secondary">Cancel</Link>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save product'}
          </button>
        </div>
      </form>

      {stockModal && (
        <AdminModal
          title={stockModal === 'restock' ? 'Restock' : 'Adjust stock'}
          onClose={() => setStockModal(null)}
        >
          <div className="admin-field">
            <label className="admin-label">
              {stockModal === 'restock' ? 'Add quantity to every size' : 'Change amount (+/- applied to M)'}
            </label>
            <input
              className="admin-input"
              type="number"
              value={stockAmount}
              onChange={e => setStockAmount(e.target.value)}
            />
          </div>
          <button type="button" className="admin-btn admin-btn-primary" onClick={applyStockChange}>
            Apply
          </button>
        </AdminModal>
      )}

      {historyOpen && (
        <AdminModal title="Stock history" onClose={() => setHistoryOpen(false)} wide>
          {stockHistory.length === 0 ? (
            <p className="admin-empty">No stock history yet.</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Size</th>
                    <th>Change</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {stockHistory.map(row => (
                    <tr key={row.id}>
                      <td>{new Date(row.created_at).toLocaleString()}</td>
                      <td>{row.size_label || '—'}</td>
                      <td>{row.change_amount > 0 ? `+${row.change_amount}` : row.change_amount}</td>
                      <td>{row.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AdminModal>
      )}
    </div>
  );
}

export default ProductEditor;
